import { useReactiveVar } from "@apollo/client";
import { useRef, useMemo } from "react";
import { useRecommendationViewItemList } from "./use-recommendation-view-item-list";
import { useRouter } from "next/router";
import type { ItemPackageProps } from "~/components/blocks/common/item-package/type";
import { LeftTag } from "~/components/blocks/common/left-tag";
import {
  PC_MIN_ITEM_WIDTH,
  SP_MIN_ITEM_WIDTH,
  PACKAGE_ASPECT_RATIO,
} from "~/components/blocks/product/common/blocks/product-related-items/const";
import { useBuildVodDetailHref } from "~/components/blocks/product/hooks/use-build-vod-detail-href";
import { modalReactiveWidthVar } from "~/components/stores/detail-product/store";
import { useImageSrcFormat } from "~/hooks/use-image-src-format";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useQueryString } from "~/hooks/use-query-string";
import { useVisitorId } from "~/hooks/use-visitor-id";
import type { G_VideoCategory } from "~/lib/graphql";
import {
  useGetRecommendedVideoQuery,
  G_VideoContentType,
  G_VideoContentTypeFilter,
  G_RecommendationEventType,
} from "~/lib/graphql";
import { sendTrackingSelectItem } from "~/lib/tracking/events";
import { isLoadingGAVar } from "~/lib/tracking/store";
import { getDevice } from "~/util/get-device";
import { isBot } from "~/util/is-bot";

type RecommendItemProps = {
  categories?: Pick<G_VideoCategory, "id">[];
  contentType?: G_VideoContentType;
};

export const useRecommendItem = (props: RecommendItemProps) => {
  const { categories, contentType } = props;

  const { buildVodDetailHref } = useBuildVodDetailHref();

  const queries = useQueryString();
  const { isReady } = useRouter();
  const { isPcSize } = useMatchMedia();
  const { imageSrcFormat } = useImageSrcFormat({ aspectRatio: "PORTRAIT" });

  const sectionRef = useRef<HTMLElement>(null);
  const isGravure = categories?.some((category) => category.id === "23");
  const modalReactiveWidth = useReactiveVar(modalReactiveWidthVar);
  const minWidth = isPcSize ? PC_MIN_ITEM_WIDTH : SP_MIN_ITEM_WIDTH;
  const displayItemCount = Math.floor(modalReactiveWidth / minWidth);

  const visitorId = useVisitorId();
  const isLoadingGA = useReactiveVar(isLoadingGAVar);

  const variables = {
    seasonId: queries.season,
    visitorId,
    device: getDevice(),
    eventType: G_RecommendationEventType.DetailPageView,
    limit: 30,
  };

  const { loading: isLoading, data } = useGetRecommendedVideoQuery({
    skip: isLoadingGA || !isReady || queries === undefined,
    ssr: isBot(),
    variables: isGravure
      ? {
          ...variables,
          categoryId: "23",
          contentType:
            contentType === G_VideoContentType.VodVr
              ? G_VideoContentTypeFilter.ContentVr
              : G_VideoContentTypeFilter.Content_2D,
        }
      : variables,
  });

  const carouselRecommendItems: ItemPackageProps[] = useMemo(() => {
    if (!data) return [];

    return data.video.i2iRecommendedVideos.edges.map(({ node }, idx) => {
      const { id, titleName, packageImage, isNewArrival, isExclusive, customTag } = node.video;
      const { href } = buildVodDetailHref({ category: node.video.rating.category, id });

      return {
        key: node.id,
        alt: titleName,
        borderRadius: "8px",
        href,
        aspectRatio: PACKAGE_ASPECT_RATIO,
        leftTag: <LeftTag isNewArrival={isNewArrival} isExclusive={isExclusive} customTag={customTag} />,
        onClick: () => {
          sendTrackingSelectItem({
            seasonId: node.id,
            itemName: titleName,
            index: idx + 1,
            itemListName: "recommend",
            itemListId: null,
            trackingId: data.video.i2iRecommendedVideos.trackingId,
            testId: data.video.i2iRecommendedVideos.testId,
            requestId: data.video.i2iRecommendedVideos.requestId,
            viaShowPosition: 1,
            viaViewRank: idx + 1,
          });
        },
        ...imageSrcFormat({ imgSrc: packageImage }),
      };
    });
  }, [data, buildVodDetailHref]);

  useRecommendationViewItemList({
    recommendedVideos: data?.video.i2iRecommendedVideos.edges ?? [],
    trackingId: data?.video.i2iRecommendedVideos.trackingId,
    testId: data?.video.i2iRecommendedVideos.testId,
    requestId: data?.video.i2iRecommendedVideos.requestId,
  });

  return { sectionRef, displayItemCount, isLoading, carouselRecommendItems };
};
